'use client'

import { useCallback, useMemo, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { VideoGrant } from 'livekit-server-sdk'
import { clientLogError } from '@/src/lib/clientLog'

import { LocalUserChoices } from '@/src'
import { ActiveRoom } from '@/src/prefabs/ActiveRoom'
import { FedorBusyScreen } from '@/src/components/FedorBusyScreen'
import {getInterviewAdmissionFailure, type InterviewAdmissionFailure} from '@/src/lib/interviewAdmission'

import { preJoinDefaults } from '@/src/constants/preJoin'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectRoomName, selectSettings } from '@/src/store/slices/meetData'
import {
  selectPhone,
  selectProject,
  selectReferrer,
  selectRoomStatus,
  setStatus,
} from '@/src/store/slices/room'

import { resolveProjectMode, shouldRunPrejoinCheck } from '@/src/constants/projectModes'

import type { MeetingUserChoices } from '@/src/hooks/useMeetingConsent'
import { useMeetingAutoRejoin } from '@/src/hooks/room/useMeetingSession'
import { recordInterviewConsentJournal } from '@/src/lib/interviewConsents'
import { RoomLookupErrorKind } from '@/src/lib/roomLookupError'
import { canJoinAfterRoomLookup } from '@/src/lib/roomLookupJoin'
import { RoomLookupErrorOverlay } from '@/src/components/RoomLookupErrorOverlay'
import { clearAccessToken } from '@/src/store/slices/token'

/**
 * Ошибка lookup комнаты со страницы, чтобы не входить в звонок без данных.
 */
interface HomeOfCustomDomainProps {
  roomLookupError?: RoomLookupErrorKind | null
}

const PreJoin = dynamic(
  async () => (await import('@/src/prefabs/PreJoin/PreJoinSettings')).JoinSetting,
  { ssr: false, loading: () => null },
)

const HomeOfCustomDomain = ({ roomLookupError = null }: HomeOfCustomDomainProps) => {
  const dispatch = useAppDispatch()
  const roomName = useAppSelector(selectRoomName)
  const status = useAppSelector(selectRoomStatus)
  const phone = useAppSelector(selectPhone)
  const project = useAppSelector(selectProject)
  const referrer = useAppSelector(selectReferrer)
  const branding = useAppSelector(selectBranding)
  const settings = useAppSelector(selectSettings)
  const { query, isReady } = useRouter()
  const shouldUseWaitingRoom = settings.wait_room || query.v === 'hr'

  const [statusWaitingRoom, setStatusWaitingRoom] = useState<boolean>(false)
  const [preJoinChoices, setPreJoinChoices] = useState<LocalUserChoices>(null)
  const [admissionFailure, setAdmissionFailure] = useState<InterviewAdmissionFailure | null>(null)
  const isInterviewPrejoin = useMemo(() => query.v === 'hr' || query.v === 'fa', [query.v])

  const handleAutoRejoin = useCallback((choices: MeetingUserChoices) => {
    setPreJoinChoices(choices)
    dispatch(setStatus('loading'))
  }, [dispatch])
  const isAutoRejoining = useMeetingAutoRejoin({
    roomName, ready: isReady,
    enabled: canJoinAfterRoomLookup(roomLookupError) && !preJoinChoices && !isInterviewPrejoin,
    onRejoin: handleAutoRejoin,
  })

  const isHidden = useMemo(() => query.hidden === 'true', [query.hidden])

  const handleAdmissionDenied = useCallback((reason: InterviewAdmissionFailure) => {
    setAdmissionFailure(reason)
    setPreJoinChoices(null)
    dispatch(clearAccessToken())
    dispatch(setStatus('prejoin'))
  }, [dispatch])

  const handlePreJoinSubmit = useCallback(
    async (values: LocalUserChoices) => {
      if (!canJoinAfterRoomLookup(roomLookupError)) {
        return
      }

      const organizer = sessionStorage.getItem('isAdmin') === 'true' || sessionStorage.getItem('isCurator') === 'true'
      if (!isInterviewPrejoin && project !== 'samolet' && !organizer && !(values as MeetingUserChoices).meetingConsent) {
        throw new Error('Перед входом подтвердите согласие в форме.')
      }

      const flagValue = process.env.NEXT_PUBLIC_MEET_BUSY_PREJOIN_ENABLED
      const busyGuardEnabled = flagValue !== 'false'
      const projectMode = resolveProjectMode(project)
      let prejoinInterviewId: string | null = null
      const isTextAssistantStaff =
        query.v === 'hr' &&
        typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem('isAdmin') === 'true' ||
          sessionStorage.getItem('isCurator') === 'true')
      const shouldCallPrejoin =
        shouldRunPrejoinCheck(projectMode) &&
        !(values as MeetingUserChoices).meetingConsent &&
        !organizer &&
        !isTextAssistantStaff &&
        !statusWaitingRoom &&
        roomName &&
        !Array.isArray(roomName)

      if (shouldCallPrejoin) {
        const submittedPhone =
          typeof (values as any)?.phone === 'string' ? String((values as any).phone) : phone
        const normalizedPhone =
          typeof submittedPhone === 'string' && submittedPhone.replace(/\D/g, '').length >= 11
            ? submittedPhone
            : undefined

        try {
          const response = await fetch('/api/prejoin_check', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              roomName,
              candidateName: values.username || '',
              phone: normalizedPhone,
              referrer,
            }),
          })

          const data = await response.json()
          prejoinInterviewId = typeof data?.interviewId === 'string' ? data.interviewId : null
          const failure = getInterviewAdmissionFailure(response.ok ? data : {
            canJoin: false, reason: 'check_unavailable',
          }, busyGuardEnabled)
          if (failure) {
            handleAdmissionDenied(failure)
            return
          }
        } catch {
          handleAdmissionDenied('check_unavailable')
          return
        }
      }

      if (roomName && !Array.isArray(roomName)) {
        await recordInterviewConsentJournal({
          roomName,
          values,
          phone,
          referrer,
          project,
          interviewId: prejoinInterviewId,
        })
      }

      setAdmissionFailure(null)
      setPreJoinChoices(values)
      dispatch(setStatus('loading'))
    },
    [dispatch, phone, project, query.v, referrer, roomName, statusWaitingRoom, roomLookupError, isInterviewPrejoin, handleAdmissionDenied],
  )

  const handlePreJoinStatus = useCallback((values) => {
    setStatusWaitingRoom(values)
  }, [])

  const handleOnError = useCallback((err: Error) => {
    console.error('error while setting up prejoin', err)
    clientLogError(`error while setting up prejoin: ${err?.message}`)
  }, [])

  const videoGrant = useMemo<VideoGrant>(
    () => ({
      canSubscribe: shouldUseWaitingRoom ? statusWaitingRoom : true,
      canPublish: isHidden ? false : shouldUseWaitingRoom ? statusWaitingRoom : true,
      roomAdmin: statusWaitingRoom,
      hidden: isHidden,
    }),
    [shouldUseWaitingRoom, statusWaitingRoom, isHidden],
  )

  return (
    <main data-lk-theme="default">
      {canJoinAfterRoomLookup(roomLookupError) &&
      roomName &&
      !Array.isArray(roomName) &&
      preJoinChoices &&
      status !== 'prejoin' ? (
        <ActiveRoom roomName={roomName} videoGrant={videoGrant} userChoices={preJoinChoices}
          onAdmissionDenied={handleAdmissionDenied} />
      ) : (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%', overflowY: 'auto' }}>
          {isAutoRejoining ? (
            <p role="status">Восстанавливаем подключение…</p>
          ) : admissionFailure ? (
            <FedorBusyScreen reason={admissionFailure} onBack={() => setAdmissionFailure(null)} />
          ) : (
            <PreJoin
              defaults={preJoinDefaults}
              parsBrandingData={branding}
              statusWaitingRoom={handlePreJoinStatus}
              onSubmit={handlePreJoinSubmit}
              onError={handleOnError}
            />
          )}
          <RoomLookupErrorOverlay kind={roomLookupError} />
        </div>
      )}
    </main>
  )
}

export default HomeOfCustomDomain
